import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Modal, Badge } from 'react-bootstrap';
import { CompanySideBar } from '../../components/CompanySideBar';
import { getVacancies, updateApplicationStatusByEmployer, getApplicationsByVacancy } from '../../services';
import { LogoSpinner } from '../../components/LogoSpinner';
import swal from 'sweetalert';

// Estilos para el modal
const modalStyles = `
    .modal-backdrop {
        z-index: 1040 !important;
    }
    .modal {
        z-index: 1050 !important;
    }
    .modal-dialog {
        z-index: 1060 !important;
    }
`;

export function CompanyApplicants() {
    const [vacancies, setVacancies] = useState([]);
    const [selectedVacancy, setSelectedVacancy] = useState(null);
    const [applicants, setApplicants] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingApplicants, setLoadingApplicants] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [selectedApplicant, setSelectedApplicant] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalApplicants, setTotalApplicants] = useState(0);
    const [limit, setLimit] = useState(10);
    const [filterStatus, setFilterStatus] = useState('');
    const [error, setError] = useState(null);

    // Cargar vacantes de la empresa
    useEffect(() => {
        loadVacancies();
    }, []);

    const loadVacancies = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await getVacancies();
            setVacancies(response.data || []);
        } catch (err) {
            setError('Error al cargar las vacantes');
            console.error('Error loading vacancies:', err);
        } finally {
            setLoading(false);
        }
    };

    // Cargar postulantes de una vacante específica con paginación
    const loadApplicants = async (vacancyId, page = 1, status = '') => {
        try {
            setLoadingApplicants(true);
            const response = await getApplicationsByVacancy(vacancyId, page, limit, status);
            setApplicants(response.data.data || response.data || []);
            setTotalPages(response.data.totalPages || 1);
            setTotalApplicants(response.data.total || response.data.data?.length || 0);
            setCurrentPage(page);
            if (!selectedVacancy) {
                setSelectedVacancy(vacancies.find(v => v.id === vacancyId));
            }
        } catch (err) {
            console.error('Error loading applicants:', err);
            swal('Error', 'No se pudieron cargar los postulantes', 'error');
        } finally {
            setLoadingApplicants(false);
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages && selectedVacancy) {
            loadApplicants(selectedVacancy.id, newPage, filterStatus);
        }
    };

    const handleStatusFilter = (status) => {
        setFilterStatus(status);
        setCurrentPage(1);
        if (selectedVacancy) {
            loadApplicants(selectedVacancy.id, 1, status);
        }
    };

    const handleViewVacancyApplicants = (vacancy) => {
        setSelectedVacancy(vacancy);
        setCurrentPage(1);
        setFilterStatus('');
        loadApplicants(vacancy.id, 1, '');
    };

    const handleBackToVacancies = () => {
        setSelectedVacancy(null);
        setApplicants([]);
        setCurrentPage(1);
        setFilterStatus('');
    };

    const handleViewApplicant = (applicant) => {
        setSelectedApplicant(applicant);
        setShowModal(true);
    };

    const handleStatusChange = async (applicationId, newStatus) => {
        const statusText = {
            'Borrador': 'borrador',
            'Aplicado': 'aplicado',
            'En Revision': 'en revisión',
            'Preseleccionado': 'preseleccionado',
            'Rechazado': 'rechazado',
            'Aceptado': 'aceptado'
        };

        swal({
            title: '¿Estás seguro?',
            text: `¿Deseas marcar este candidato como ${statusText[newStatus]}?`,
            icon: 'warning',
            buttons: ['Cancelar', 'Confirmar'],
            dangerMode: newStatus === 'RECHAZADO',
        }).then(async (willChange) => {
            if (willChange) {
                try {
                    setLoadingApplicants(true);
                    await updateApplicationStatusByEmployer(applicationId, newStatus);

                    // Por ahora solo actualizamos localmente
                    setApplicants(applicants.map(app =>
                        app.id === applicationId
                            ? { ...app, estado: newStatus }
                            : app
                    ));

                    swal('¡Actualizado!', `El estado del candidato ha sido actualizado a ${statusText[newStatus]}`, 'success');
                } catch (error) {
                    console.error('Error updating status:', error);
                    swal('Error', 'No se pudo actualizar el estado del candidato', 'error');
                } finally {
                    setLoadingApplicants(false);
                }
            }
        });
    };

    const getStatusBadge = (estado) => {
        const statusConfig = {
            'Borrador': { bg: 'secondary', text: 'Borrador' },
            'Aplicado': { bg: 'primary', text: 'Aplicado' },
            'En Revision': { bg: 'warning', text: 'En Revisión' },
            'Preseleccionado': { bg: 'info', text: 'Preseleccionado ⭐' },
            'Rechazado': { bg: 'danger', text: 'Rechazado' },
            'Aceptado': { bg: 'success', text: 'Aceptado' }
        };
        const config = statusConfig[estado] || { bg: 'secondary', text: estado };
        return <Badge bg={config.bg}>{config.text}</Badge>;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('es-DO', { year: 'numeric', month: 'long', day: 'numeric' });
    };

    if (loading) {
        return <LogoSpinner />;
    }

    return (
        <>
            {/* Inyectar estilos para el modal */}
            <style>{modalStyles}</style>

            <div className="page-content bg-white">
                <div className="content-block">
                    <div className="section-full bg-white browse-job p-t50 p-b20">
                        <div className="container">
                            <div className="row">
                                <CompanySideBar active="applicants" />
                                <div className="col-xl-9 col-lg-8 m-b30">
                                    <div className="job-bx-title clearfix">
                                        <h5 className="font-weight-700 pull-left text-uppercase">
                                            Postulantes por Vacante
                                        </h5>
                                    </div>

                                    {error && (
                                        <div className="alert alert-danger" role="alert">
                                            {error}
                                        </div>
                                    )}

                                    {!selectedVacancy ? (
                                        <>
                                            <div className="alert alert-info">
                                                Selecciona una vacante para ver los postulantes que han aplicado.
                                            </div>

                                            {vacancies.length === 0 ? (
                                                <div className="text-center p-5">
                                                    <i className="fa fa-briefcase" style={{ fontSize: '64px', color: '#ccc' }}></i>
                                                    <h4 className="m-t20">No tienes vacantes publicadas</h4>
                                                    <p className="text-muted">Comienza publicando tu primera vacante</p>
                                                    <Link to="/company-post-jobs" className="site-button m-t20">
                                                        Publicar Vacante
                                                    </Link>
                                                </div>
                                            ) : (
                                                <div className="row">
                                                    {vacancies.map((vacancy) => (
                                                        <div key={vacancy.id} className="col-lg-6 col-md-12 m-b30">
                                                            <div className="job-bx" style={{ border: '1px solid #e9ecef', borderRadius: '8px', padding: '20px' }}>
                                                                <div className="d-flex align-items-center justify-content-between m-b15">
                                                                    <h5 className="m-0">{vacancy.titulo}</h5>
                                                                    {vacancy.activo ? (
                                                                        <Badge bg="success">Activa</Badge>
                                                                    ) : (
                                                                        <Badge bg="secondary">Inactiva</Badge>
                                                                    )}
                                                                </div>
                                                                <p className="text-muted m-b15" style={{ fontSize: '14px' }}>
                                                                    <i className="fa fa-map-marker m-r5"></i>
                                                                    {vacancy.ciudad?.nombre || 'N/A'}, {vacancy.pais?.nombre || 'N/A'}
                                                                </p>
                                                                <p className="text-muted m-b15" style={{ fontSize: '14px' }}>
                                                                    <i className="fa fa-calendar m-r5"></i>
                                                                    Publicada: {formatDate(vacancy.fechaPublicacion)}
                                                                </p>
                                                                <div className="d-flex align-items-center justify-content-between">
                                                                    <span className="text-primary font-weight-600">
                                                                        <i className="fa fa-users m-r5"></i>
                                                                        {vacancy.totalAplicaciones || 0} Postulantes
                                                                    </span>
                                                                    <button
                                                                        onClick={() => handleViewVacancyApplicants(vacancy)}
                                                                        className="site-button"
                                                                        style={{ padding: '8px 20px' }}
                                                                    >
                                                                        Ver Postulantes
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            <div className="m-b20">
                                                <button
                                                    onClick={handleBackToVacancies}
                                                    className="site-button outline-primary"
                                                >
                                                    <i className="fa fa-arrow-left m-r5"></i> Volver a Vacantes
                                                </button>
                                            </div>

                                            <div className="job-bx-title clearfix m-b20">
                                                <h5 className="font-weight-700 text-uppercase">
                                                    Postulantes para: {selectedVacancy.titulo}
                                                </h5>
                                            </div>

                                            {/* Filtros y estadísticas */}
                                            <div className="d-flex justify-content-between align-items-center mb-3 p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                                                <div>
                                                    <span className="mr-3"><strong>Total:</strong> {totalApplicants} postulantes</span>
                                                    <span className="text-muted">Página {currentPage} de {totalPages}</span>
                                                </div>
                                                <div className="d-flex" style={{ gap: '8px' }}>
                                                    <button
                                                        className={`btn btn-sm ${filterStatus === '' ? 'btn-primary' : 'btn-outline-primary'}`}
                                                        onClick={() => handleStatusFilter('')}
                                                    >
                                                        Todos
                                                    </button>
                                                    <button
                                                        className={`btn btn-sm ${filterStatus === 'Aplicado' ? 'btn-primary' : 'btn-outline-primary'}`}
                                                        onClick={() => handleStatusFilter('Aplicado')}
                                                    >
                                                        Aplicados
                                                    </button>
                                                    <button
                                                        className={`btn btn-sm ${filterStatus === 'En Revision' ? 'btn-warning' : 'btn-outline-warning'}`}
                                                        onClick={() => handleStatusFilter('En Revision')}
                                                    >
                                                        En Revisión
                                                    </button>
                                                    <button
                                                        className={`btn btn-sm ${filterStatus === 'Preseleccionado' ? 'btn-info' : 'btn-outline-info'}`}
                                                        onClick={() => handleStatusFilter('Preseleccionado')}
                                                    >
                                                        Preseleccionados ⭐
                                                    </button>
                                                    <button
                                                        className={`btn btn-sm ${filterStatus === 'Aceptado' ? 'btn-success' : 'btn-outline-success'}`}
                                                        onClick={() => handleStatusFilter('Aceptado')}
                                                    >
                                                        Aceptados
                                                    </button>
                                                    <button
                                                        className={`btn btn-sm ${filterStatus === 'Rechazado' ? 'btn-danger' : 'btn-outline-danger'}`}
                                                        onClick={() => handleStatusFilter('Rechazado')}
                                                    >
                                                        Rechazados
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="job-bx bg-white m-b30" style={{ padding: '20px', borderRadius: '8px' }}>
                                                <h4 className="m-b10">{selectedVacancy.titulo}</h4>
                                                <p className="text-muted">
                                                    <i className="fa fa-map-marker m-r5"></i>
                                                    {selectedVacancy.ciudad?.nombre}, {selectedVacancy.pais?.nombre}
                                                </p>
                                            </div>

                                            {loadingApplicants ? (
                                                <div className="text-center p-5">
                                                    <div className="spinner-border text-primary" role="status">
                                                        <span className="sr-only">Cargando...</span>
                                                    </div>
                                                </div>
                                            ) : applicants.length === 0 ? (
                                                <div className="text-center p-5 bg-white" style={{ borderRadius: '8px' }}>
                                                    <i className="fa fa-users" style={{ fontSize: '64px', color: '#ccc' }}></i>
                                                    <h4 className="m-t20">No hay postulantes aún</h4>
                                                    <p className="text-muted">Esta vacante aún no tiene aplicaciones</p>
                                                </div>
                                            ) : (
                                                <div className="table-responsive">
                                                    <table className="table table-hover">
                                                        <thead>
                                                            <tr>
                                                                <th>Candidato</th>
                                                                <th>Correo</th>
                                                                <th>Teléfono</th>
                                                                <th>Estado</th>
                                                                <th>Fecha Aplicación</th>
                                                                <th>Acciones</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {applicants.map((application) => (
                                                                <tr key={application.id}>
                                                                    <td>
                                                                        <div className="d-flex align-items-center">
                                                                            <div className="mr-3">
                                                                                <i className="fa fa-user-circle" style={{ fontSize: '32px', color: '#009247' }}></i>
                                                                            </div>
                                                                            <div>
                                                                                <strong>
                                                                                    {application.user?.nombre} {application.user?.apellido}
                                                                                </strong>
                                                                                <br />
                                                                                <small className="text-muted">
                                                                                    {application.user?.perfilUsuario?.tituloProfesional || 'Sin título'}
                                                                                </small>
                                                                            </div>
                                                                        </div>
                                                                    </td>
                                                                    <td>{application.user?.email || 'N/A'}</td>
                                                                    <td>{application.user?.perfilUsuario?.telefono || 'N/A'}</td>
                                                                    <td>{getStatusBadge(application.estado)}</td>
                                                                    <td>{formatDate(application.fechaAplicacion)}</td>
                                                                    <td>
                                                                        <div className="d-flex flex-wrap" style={{ gap: '5px' }}>
                                                                            <button
                                                                                onClick={() => handleViewApplicant(application)}
                                                                                className="btn btn-info btn-sm"
                                                                                title="Ver perfil completo"
                                                                            >
                                                                                <i className="fa fa-eye"></i>
                                                                            </button>
                                                                            <button
                                                                                onClick={() => handleStatusChange(application.id, 'En Revision')}
                                                                                className="btn btn-warning btn-sm"
                                                                                title="Poner en revisión"
                                                                                disabled={application.estado === 'En Revision'}
                                                                            >
                                                                                <i className="fa fa-hourglass-half"></i>
                                                                            </button>
                                                                            <button
                                                                                onClick={() => handleStatusChange(application.id, 'Preseleccionado')}
                                                                                className="btn btn-info btn-sm"
                                                                                title="Marcar como preseleccionado"
                                                                                disabled={application.estado === 'Preseleccionado'}
                                                                            >
                                                                                <i className="fa fa-star"></i>
                                                                            </button>
                                                                            <button
                                                                                onClick={() => handleStatusChange(application.id, 'Aceptado')}
                                                                                className="btn btn-success btn-sm"
                                                                                title="Aceptar candidato"
                                                                                disabled={application.estado === 'Aceptado'}
                                                                            >
                                                                                <i className="fa fa-check"></i>
                                                                            </button>
                                                                            <button
                                                                                onClick={() => handleStatusChange(application.id, 'Rechazado')}
                                                                                className="btn btn-danger btn-sm"
                                                                                title="Rechazar candidato"
                                                                                disabled={application.estado === 'Rechazado'}
                                                                            >
                                                                                <i className="fa fa-times"></i>
                                                                            </button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}

                                            {/* Paginación */}
                                            {!loadingApplicants && applicants.length > 0 && totalPages > 1 && (
                                                <div className="pagination-bx m-t30 text-center">
                                                    <ul className="pagination justify-content-center">
                                                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                            <button
                                                                className="page-link"
                                                                onClick={() => handlePageChange(currentPage - 1)}
                                                                disabled={currentPage === 1}
                                                            >
                                                                <i className="ti-arrow-left"></i> Anterior
                                                            </button>
                                                        </li>
                                                        {[...Array(totalPages)].map((_, index) => {
                                                            const pageNum = index + 1;
                                                            // Mostrar páginas cercanas a la actual
                                                            if (
                                                                pageNum === 1 ||
                                                                pageNum === totalPages ||
                                                                (pageNum >= currentPage - 2 && pageNum <= currentPage + 2)
                                                            ) {
                                                                return (
                                                                    <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                                                                        <button
                                                                            className="page-link"
                                                                            onClick={() => handlePageChange(pageNum)}
                                                                        >
                                                                            {pageNum}
                                                                        </button>
                                                                    </li>
                                                                );
                                                            } else if (
                                                                pageNum === currentPage - 3 ||
                                                                pageNum === currentPage + 3
                                                            ) {
                                                                return <li key={pageNum} className="page-item disabled"><span className="page-link">...</span></li>;
                                                            }
                                                            return null;
                                                        })}
                                                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                            <button
                                                                className="page-link"
                                                                onClick={() => handlePageChange(currentPage + 1)}
                                                                disabled={currentPage === totalPages}
                                                            >
                                                                Siguiente <i className="ti-arrow-right"></i>
                                                            </button>
                                                        </li>
                                                    </ul>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal de Detalle del Postulante */}
            <Modal
                show={showModal}
                onHide={() => setShowModal(false)}
                size="lg"
                centered
                backdrop="static"
                style={{ zIndex: 9999 }}
            >
                <Modal.Header closeButton style={{ backgroundColor: '#009247', color: 'white' }}>
                    <Modal.Title>
                        <i className="fa fa-user-circle mr-2"></i> Perfil del Candidato
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    {selectedApplicant && selectedApplicant.user ? (
                        <div className="container-fluid">
                            {/* Encabezado del candidato */}
                            <div className="row mb-4 pb-3 border-bottom">
                                <div className="col-12 text-center">
                                    <div className="mb-3">
                                        <img src={selectedApplicant.user.perfilUsuario?.fotoPerfil || undefined} alt="Foto de perfil" style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', background: '#f0f0f0' }} onError={e => { e.target.style.display = 'none'; }} />
                                    </div>
                                    <h4 className="mb-1">
                                        {selectedApplicant.user.nombre} {selectedApplicant.user.apellido}
                                    </h4>
                                    <p className="text-muted mb-2">
                                        {selectedApplicant.user.perfilUsuario?.tituloProfesional || 'Sin título profesional'}
                                    </p>
                                    <div className="mb-2">
                                        {getStatusBadge(selectedApplicant.estado)}
                                    </div>
                                </div>
                            </div>

                            {/* Información general de la aplicación */}
                            <div className="row mb-3">
                                <div className="col-12">
                                    <h5 className="text-primary mb-3">
                                        <i className="fa fa-info-circle mr-2"></i> Datos de la Aplicación
                                    </h5>
                                </div>
                                <div className="col-12">
                                    <div className="table-responsive">
                                        <table className="table table-bordered table-sm">
                                            <tbody>

                                                <tr>
                                                    <th>Estado</th>
                                                    <td>{selectedApplicant.estado}</td>
                                                </tr>
                                                <tr>
                                                    <th>Fecha de Aplicación</th>
                                                    <td>{formatDate(selectedApplicant.fechaAplicacion)}</td>
                                                </tr>

                                                {selectedApplicant.applicationResume && typeof selectedApplicant.applicationResume === 'string' && selectedApplicant.applicationResume.endsWith('.pdf') && (
                                                    <tr>
                                                        <th>Resumen de Aplicación (PDF)</th>
                                                        <td>
                                                            <a href={selectedApplicant.applicationResume} target="_blank" rel="noopener noreferrer" className="btn btn-outline-primary btn-sm">
                                                                <i className="fa fa-file-pdf-o mr-1"></i> Ver/Descargar PDF
                                                            </a>
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>

                            {/* Información de contacto */}
                            <div className="row mb-3">
                                <div className="col-12">
                                    <h5 className="text-primary mb-3">
                                        <i className="fa fa-address-card mr-2"></i> Información de Contacto
                                    </h5>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <div className="d-flex align-items-start">
                                        <i className="fa fa-envelope text-primary mr-2 mt-1"></i>
                                        <div>
                                            <small className="text-muted d-block">Correo Electrónico</small>
                                            <strong>{selectedApplicant.user.email || 'N/A'}</strong>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <div className="d-flex align-items-start">
                                        <i className="fa fa-phone text-primary mr-2 mt-1"></i>
                                        <div>
                                            <small className="text-muted d-block">Teléfono</small>
                                            <strong>{selectedApplicant.user.perfilUsuario?.telefono || 'N/A'}</strong>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <div className="d-flex align-items-start">
                                        <i className="fa fa-map-marker text-primary mr-2 mt-1"></i>
                                        <div>
                                            <small className="text-muted d-block">Ubicación</small>
                                            <strong>
                                                {selectedApplicant.user.perfilUsuario?.ciudadResidencia?.nombre || 'N/A'}
                                                {selectedApplicant.user.perfilUsuario?.ciudadResidencia?.nombre &&
                                                    selectedApplicant.user.perfilUsuario?.paisResidencia?.nombre ? ', ' : ''}
                                                {selectedApplicant.user.perfilUsuario?.paisResidencia?.nombre || ''}
                                            </strong>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <div className="d-flex align-items-start">
                                        <i className="fa fa-money text-primary mr-2 mt-1"></i>
                                        <div>
                                            <small className="text-muted d-block">Salario Deseado</small>
                                            <strong>
                                                {selectedApplicant.user.perfilUsuario?.salarioDeseado
                                                    ? `RD$ ${selectedApplicant.user.perfilUsuario.salarioDeseado.toLocaleString()}`
                                                    : 'No especificado'}
                                            </strong>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Redes sociales y enlaces */}
                            {(selectedApplicant.user.perfilUsuario?.linkedin ||
                                selectedApplicant.user.perfilUsuario?.github ||
                                selectedApplicant.user.perfilUsuario?.portfolio) && (
                                    <div className="row mb-3">
                                        <div className="col-12">
                                            <h5 className="text-primary mb-3">
                                                <i className="fa fa-link mr-2"></i> Enlaces y Redes Sociales
                                            </h5>
                                        </div>
                                        <div className="col-12">
                                            <div className="d-flex flex-wrap" style={{ gap: '10px' }}>
                                                {selectedApplicant.user.perfilUsuario?.linkedin && (
                                                    <a
                                                        href={selectedApplicant.user.perfilUsuario.linkedin}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="btn btn-primary btn-sm"
                                                    >
                                                        <i className="fa fa-linkedin mr-1"></i> LinkedIn
                                                    </a>
                                                )}
                                                {selectedApplicant.user.perfilUsuario?.github && (
                                                    <a
                                                        href={selectedApplicant.user.perfilUsuario.github}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="btn btn-dark btn-sm"
                                                    >
                                                        <i className="fa fa-github mr-1"></i> GitHub
                                                    </a>
                                                )}
                                                {selectedApplicant.user.perfilUsuario?.portfolio && (
                                                    <a
                                                        href={selectedApplicant.user.perfilUsuario.portfolio}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="btn btn-info btn-sm"
                                                    >
                                                        <i className="fa fa-globe mr-1"></i> Portfolio
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                            {/* Curriculum completo (perfilUsuario) */}
                            {selectedApplicant.user.perfilUsuario && (
                                <>
                                <div className="row mb-3">
                                    <div className="col-12">
                                        <h5 className="text-primary mb-3">
                                            <i className="fa fa-file-text-o mr-2"></i> Curriculum Completo
                                        </h5>
                                        <div className="table-responsive">
                                            <table className="table table-bordered table-sm">
                                                <tbody>
                                                    {/* <tr>
                                                        <th>ID</th>
                                                        <td>{selectedApplicant.user.perfilUsuario.id}</td>
                                                    </tr> */}
                                                    <tr>
                                                        <th>Teléfono</th>
                                                        <td>{selectedApplicant.user.perfilUsuario.telefono || 'N/A'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Dirección</th>
                                                        <td>{selectedApplicant.user.perfilUsuario.direccion || 'N/A'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Título Profesional</th>
                                                        <td>{selectedApplicant.user.perfilUsuario.tituloProfesional || 'N/A'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Fecha de Nacimiento</th>
                                                        <td>{selectedApplicant.user.perfilUsuario.fechaNacimiento ? formatDate(selectedApplicant.user.perfilUsuario.fechaNacimiento) : 'N/A'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Salario Actual</th>
                                                        <td>{selectedApplicant.user.perfilUsuario.salarioActual ? `RD$ ${selectedApplicant.user.perfilUsuario.salarioActual.toLocaleString()}` : 'N/A'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Salario Deseado</th>
                                                        <td>{selectedApplicant.user.perfilUsuario.salarioDeseado ? `RD$ ${selectedApplicant.user.perfilUsuario.salarioDeseado.toLocaleString()}` : 'N/A'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Idiomas</th>
                                                        <td>{selectedApplicant.user.perfilUsuario.idiomas || 'N/A'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Género</th>
                                                        <td>{selectedApplicant.user.perfilUsuario.genero || 'N/A'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Teléfono de Contacto</th>
                                                        <td>{selectedApplicant.user.perfilUsuario.telefonoContacto || 'N/A'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Pais de Residencia</th>
                                                        <td>{selectedApplicant.user.perfilUsuario.paisResidencia?.nombre || 'N/A'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Ciudad de Residencia</th>
                                                        <td>{selectedApplicant.user.perfilUsuario.ciudadResidencia?.nombre || 'N/A'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>LinkedIn</th>
                                                        <td>{selectedApplicant.user.perfilUsuario.linkedin ? <a href={selectedApplicant.user.perfilUsuario.linkedin} target="_blank" rel="noopener noreferrer">{selectedApplicant.user.perfilUsuario.linkedin}</a> : 'N/A'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Facebook</th>
                                                        <td>{selectedApplicant.user.perfilUsuario.facebook ? <a href={selectedApplicant.user.perfilUsuario.facebook} target="_blank" rel="noopener noreferrer">{selectedApplicant.user.perfilUsuario.facebook}</a> : 'N/A'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Instagram</th>
                                                        <td>{selectedApplicant.user.perfilUsuario.instagram ? <a href={selectedApplicant.user.perfilUsuario.instagram} target="_blank" rel="noopener noreferrer">{selectedApplicant.user.perfilUsuario.instagram}</a> : 'N/A'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>GitHub</th>
                                                        <td>{selectedApplicant.user.perfilUsuario.github ? <a href={selectedApplicant.user.perfilUsuario.github} target="_blank" rel="noopener noreferrer">{selectedApplicant.user.perfilUsuario.github}</a> : 'N/A'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>X (Twitter)</th>
                                                        <td>{selectedApplicant.user.perfilUsuario.x ? <a href={selectedApplicant.user.perfilUsuario.x} target="_blank" rel="noopener noreferrer">{selectedApplicant.user.perfilUsuario.x}</a> : 'N/A'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Foto de Perfil</th>
                                                        <td>{selectedApplicant.user.perfilUsuario.fotoPerfil ? <a href={selectedApplicant.user.perfilUsuario.fotoPerfil} target="_blank" rel="noopener noreferrer">Ver Foto</a> : 'N/A'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Resumen Profesional</th>
                                                        <td style={{ whiteSpace: 'pre-line' }}>{selectedApplicant.user.perfilUsuario.resumen || 'N/A'}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                                </>
                            )}
                            {/* Curriculum enviado con la aplicación */}
                            {selectedApplicant.applicationResume && typeof selectedApplicant.applicationResume === 'object' && (
                                <>
                                    {/* Resumen profesional */}
                                    {selectedApplicant.applicationResume.resumen && (
                                        <div className="row mb-3">
                                            <div className="col-12">
                                                <h5 className="text-primary mb-3">
                                                    <i className="fa fa-align-left mr-2"></i> Resumen Profesional
                                                </h5>
                                                <p style={{ whiteSpace: 'pre-line' }}>{selectedApplicant.applicationResume.resumen}</p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Habilidades */}
                                    {selectedApplicant.applicationResume.habilidades && (
                                        <div className="row mb-3">
                                            <div className="col-12">
                                                <h5 className="text-primary mb-3">
                                                    <i className="fa fa-tags mr-2"></i> Habilidades
                                                </h5>
                                                <p style={{ whiteSpace: 'pre-line' }}>{
                                                    Array.isArray(selectedApplicant.applicationResume.habilidades)
                                                        ? selectedApplicant.applicationResume.habilidades.join(', ')
                                                        : selectedApplicant.applicationResume.habilidades
                                                }</p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Experiencia Laboral */}
                                    {selectedApplicant.applicationResume.laboralExperiences?.length > 0 && (
                                        <div className="row mb-3">
                                            <div className="col-12">
                                                <h5 className="text-primary mb-3">
                                                    <i className="fa fa-briefcase mr-2"></i> Experiencia Laboral
                                                </h5>
                                                {selectedApplicant.applicationResume.laboralExperiences.map((exp, i) => (
                                                    <div key={i} className="mb-3 p-3 border rounded">
                                                        <strong>{exp.cargo || exp.titulo || exp.position || 'Cargo no especificado'}</strong>
                                                        <span className="text-muted"> — {exp.empresa || exp.company || 'Empresa no especificada'}</span>
                                                        {(exp.fechaInicio || exp.startDate) && (
                                                            <div><small className="text-muted">{exp.fechaInicio || exp.startDate} – {exp.fechaFin || exp.endDate || 'Presente'}</small></div>
                                                        )}
                                                        {(exp.descripcion || exp.description) && (
                                                            <p className="mt-2 mb-0" style={{ whiteSpace: 'pre-line', fontSize: '14px' }}>{exp.descripcion || exp.description}</p>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Educación */}
                                    {selectedApplicant.applicationResume.educations?.length > 0 && (
                                        <div className="row mb-3">
                                            <div className="col-12">
                                                <h5 className="text-primary mb-3">
                                                    <i className="fa fa-graduation-cap mr-2"></i> Educación
                                                </h5>
                                                {selectedApplicant.applicationResume.educations.map((edu, i) => (
                                                    <div key={i} className="mb-3 p-3 border rounded">
                                                        <strong>{edu.titulo || edu.degree || edu.carrera || 'Título no especificado'}</strong>
                                                        <span className="text-muted"> — {edu.institucion || edu.institution || 'Institución no especificada'}</span>
                                                        {(edu.fechaInicio || edu.startDate) && (
                                                            <div><small className="text-muted">{edu.fechaInicio || edu.startDate} – {edu.fechaFin || edu.endDate || 'Presente'}</small></div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Proyectos */}
                                    {selectedApplicant.applicationResume.projects?.length > 0 && (
                                        <div className="row mb-3">
                                            <div className="col-12">
                                                <h5 className="text-primary mb-3">
                                                    <i className="fa fa-code mr-2"></i> Proyectos
                                                </h5>
                                                {selectedApplicant.applicationResume.projects.map((proj, i) => (
                                                    <div key={i} className="mb-3 p-3 border rounded">
                                                        <strong>{proj.nombre || proj.name || proj.titulo || 'Proyecto sin nombre'}</strong>
                                                        {proj.url && (
                                                            <span> — <a href={proj.url} target="_blank" rel="noopener noreferrer">{proj.url}</a></span>
                                                        )}
                                                        {(proj.descripcion || proj.description) && (
                                                            <p className="mt-2 mb-0" style={{ whiteSpace: 'pre-line', fontSize: '14px' }}>{proj.descripcion || proj.description}</p>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}

                        </div>
                    ) : (
                        <div className="text-center p-5">
                            <i className="fa fa-exclamation-circle" style={{ fontSize: '48px', color: '#ccc' }}></i>
                            <p className="mt-3">No se pudo cargar la información del candidato</p>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-between">
                    <div className="d-flex" style={{ gap: '10px' }}>
                        {selectedApplicant && selectedApplicant.estado !== 'En Revision' && (
                            <button
                                className="btn btn-warning"
                                onClick={() => {
                                    handleStatusChange(selectedApplicant.id, 'En Revision');
                                    setShowModal(false);
                                }}
                            >
                                <i className="fa fa-hourglass-half mr-1"></i> Poner en Revisión
                            </button>
                        )}
                        {selectedApplicant && selectedApplicant.estado !== 'Aceptado' && (
                            <button
                                className="btn btn-success"
                                onClick={() => {
                                    handleStatusChange(selectedApplicant.id, 'Aceptado');
                                    setShowModal(false);
                                }}
                            >
                                <i className="fa fa-check mr-1"></i> Aceptar
                            </button>
                        )}
                        {selectedApplicant && selectedApplicant.estado !== 'Rechazado' && (
                            <button
                                className="btn btn-danger"
                                onClick={() => {
                                    handleStatusChange(selectedApplicant.id, 'Rechazado');
                                    setShowModal(false);
                                }}
                            >
                                <i className="fa fa-times mr-1"></i> Rechazar
                            </button>
                        )}
                    </div>
                    <button className="site-button outline-primary" onClick={() => setShowModal(false)}>
                        <i className="fa fa-times mr-1"></i> Cerrar
                    </button>
                </Modal.Footer>
            </Modal>
        </>
    );
}
